import { useState } from 'react';
import {
  Pressable,
  Text,
  TextInput,
  View,
  type TextInputProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Eye, EyeOff } from 'lucide-react-native';

import { colors, radius, typography } from '@/theme/tokens';

interface Props extends Omit<TextInputProps, 'style'> {
  label?: string;
  hint?: string;
  error?: string | null;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  password?: boolean;
}

export function TextField({
  label,
  hint,
  error,
  iconLeft,
  iconRight,
  containerStyle,
  password,
  secureTextEntry,
  onFocus,
  onBlur,
  ...rest
}: Props) {
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(true);
  const isSecure = password ? hidden : secureTextEntry;

  const borderColor = error
    ? colors.danger[500]
    : focused
      ? colors.magenta[500]
      : colors.ink[200];

  return (
    <View style={[{ gap: 6 }, containerStyle]}>
      {label ? (
        <Text
          style={{
            fontSize: 13,
            fontWeight: typography.weights.semibold,
            color: colors.ink[700],
            letterSpacing: 0.1,
          }}
        >
          {label}
        </Text>
      ) : null}

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          paddingHorizontal: 14,
          height: 50,
          borderWidth: 1.5,
          borderColor,
          borderRadius: radius.xl,
          backgroundColor: colors.white,
          // @ts-ignore web only
          transitionProperty: 'border-color, box-shadow',
          // @ts-ignore web only
          transitionDuration: '160ms',
          ...(focused && !error
            ? {
                // @ts-ignore web only
                boxShadow: `0 0 0 4px ${colors.magenta[100]}`,
              }
            : null),
        }}
      >
        {iconLeft ? <View>{iconLeft}</View> : null}
        <TextInput
          {...rest}
          secureTextEntry={isSecure}
          placeholderTextColor={colors.ink[400]}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          style={{
            flex: 1,
            fontSize: typography.sizes.md,
            color: colors.ink[900],
            fontFamily: typography.body.fontFamily,
            // @ts-ignore web only
            outlineStyle: 'none',
          }}
        />
        {password ? (
          <Pressable hitSlop={8} onPress={() => setHidden((h) => !h)}>
            {hidden ? <EyeOff size={18} color={colors.ink[400]} /> : <Eye size={18} color={colors.ink[600]} />}
          </Pressable>
        ) : iconRight ? (
          <View>{iconRight}</View>
        ) : null}
      </View>

      {error ? (
        <Text style={{ fontSize: 12, color: colors.danger[600], fontWeight: typography.weights.semibold }}>{error}</Text>
      ) : hint ? (
        <Text style={{ fontSize: 12, color: colors.ink[500] }}>{hint}</Text>
      ) : null}
    </View>
  );
}
